import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import SearchIcon from 'assets/icons/search_icon.svg?react';
import ClearIcon from '@mui/icons-material/Clear';
import { SkillCategoryIcon } from 'utils/skillCategoryIcons';
import skillsEndpoint from 'endPoints/skills';
import useDataListFactory from 'services/dataList';
import LinearProgress from 'components/LinearProgress';
import { IS_SNAP, SNAP_SHOT_LIMIT } from 'variables/main';
import parseQS from 'helpers/parseQS';
import { getLanguage } from 'translation';
import { getLocalizedPath } from 'helpers/getLocale';
import { getSkillSlug } from 'utils/seo';
import CardGridSkeleton from 'components/CardGridSkeleton';

const Helmet = React.lazy(() => import('components/Helmet'));
const Header = React.lazy(() => import('components/Header'));
const Footer = React.lazy(() => import('components/Footer'));
const MainLayout = React.lazy(() => import('layouts/MainLayout'));
const GalaxyView = React.lazy(() =>
  import('components/SkillsVisualizations/GalaxyView')
);

const useDataList = (props, options) => useDataListFactory(props, options);

const Background = styled('section')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: 'calc(100vh - 235px)',
  overflowX: 'hidden' // Prevent horizontal scroll
}));

const VisualizationContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%', // Avoid scrollbar issues
  // Fallback for browsers that don't support dvh
  height: '100vh',
  // Use dynamic viewport height where supported (mobile address bar, etc.)
  '@supports (height: 100dvh)': {
    height: '100dvh'
  },
  zIndex: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    height: '-webkit-fill-available' // Fallback for older mobile browsers
  }
}));

const OverlayContent = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  padding: '40px 100px',
  width: '100%',
  boxSizing: 'border-box',
  pointerEvents: 'none',
  [theme.breakpoints.down('lg')]: {
    padding: '50px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '30px'
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '20px', // Match logo left padding (HeaderWrapperInner has padding: '20px' on mobile)
    paddingRight: '20px',
    paddingTop: '20px', // Move h1 more to top
    paddingBottom: '15px',
    width: '100%',
    maxWidth: '100%'
  }
}));


const Title = styled(Typography)(({ theme, visible }) => ({
  marginTop: 20,
  marginBottom: 0,
  marginLeft: 10,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  pointerEvents: 'auto',
  opacity: visible ? 1 : 0,
  transition: 'opacity 1s ease-in-out',
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginTop: 15,
    marginLeft: 0
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36px',
    marginTop: 0,
    paddingTop: 0,
    marginLeft: 0
  }
}));

const ContentBelow = styled('div')(({ theme, isSnap }) => ({
  position: 'relative',
  zIndex: 1,
  padding: '40px 100px',
  paddingTop: 0, // Remove top padding
  backgroundColor: theme.palette.white.main,
  // In react-snap we don't render the fixed visualization, so don't push content below 100vh.
  marginTop: isSnap ? 0 : '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('lg')]: {
    padding: 50,
    paddingTop: 0,
    marginTop: isSnap ? 0 : '100vh'
  },
  [theme.breakpoints.down('md')]: {
    padding: 30,
    paddingTop: 0,
    marginTop: isSnap ? 0 : '100vh'
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20,
    paddingTop: 0,
    // Fallback for browsers that don't support dvh
    marginTop: isSnap ? 0 : '100vh',
    // Use dynamic viewport height on mobile where supported
    '@supports (height: 100dvh)': {
      marginTop: isSnap ? 0 : '100dvh'
    },
    width: '100%',
    maxWidth: '100%'
  }
}));

const ActionsWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 40,
  marginTop: 0
}));

const VisualizationSection = styled('div')(() => ({
  width: '100%',
  height: '100%',
  position: 'relative'
}));

const ScrollIndicator = styled('div')(({ theme, visible }) => ({
  position: 'absolute',
  bottom: 30,
  left: '50%',
  transform: 'translateX(-50%) rotate(90deg)',
  zIndex: 5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  pointerEvents: 'none',
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.5s ease-in',
  [theme.breakpoints.down('sm')]: {
    bottom: 20
  }
}));

const ScrollIndicatorOval = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 4,
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 12px',
  borderRadius: '20px',
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: 'rgba(255, 255, 255, 0.8)'
}));

const ScrollDot = styled('div')(({ theme, delay }) => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: `scrollPulse 1.4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  '@keyframes scrollPulse': {
    '0%, 100%': {
      opacity: 0.3,
      transform: 'scale(1)'
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.2)'
    }
  }
}));

const TextFieldStyled = styled(TextField)(() => ({
  borderRadius: 8,
  marginTop: 0,
  '& .MuiOutlinedInput-root': {
    marginTop: 0
  }
}));

const ButtonStyled = styled(Button)(() => ({
  display: 'block',
  margin: '40px auto 0'
}));

const ClearIconStyled = styled(ClearIcon)(({ theme }) => ({
  cursor: 'pointer',
  marginRight: 10,
  color: theme.palette.textPrimary.main
}));

const SkillsList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: 15,
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '100%',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 10
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    width: '100%',
    maxWidth: '100%'
  }
}));

const SkillItem = styled('li')(({ theme }) => ({
  margin: 0
}));

const SkillLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '15px', // Smaller left padding
  textDecoration: 'none',
  color: theme.palette.textPrimary.main,
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  height: '72px', // Fixed height for 3 rows of text (24px line height * 3 = 72px)
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.white.main,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    '& svg': {
      filter: 'brightness(0) invert(1)'
    }
  }
}));

const SkillName = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.2, // Tighter line height for 3 rows
  margin: 0,
  display: '-webkit-box',
  WebkitLineClamp: 3, // Limit to 3 lines
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
  alignSelf: 'center', // Vertically center
  [theme.breakpoints.down('sm')]: {
    fontSize: 14
  }
}));

const Skills = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [urlFilters] = React.useState(parseQS(location.search));
  const t = useTranslate('Skills');
  const [lang] = React.useState(() => getLanguage().toUpperCase());
  // Avoid long-running timers/animations during react-snap prerendering
  const [titleVisible, setTitleVisible] = React.useState(IS_SNAP);
  const [scrollIndicatorVisible, setScrollIndicatorVisible] = React.useState(IS_SNAP);

  const [qsPage] = React.useState(() => {
    if (!IS_SNAP) return null;
    const pageNumber = (location?.pathname || '').split('/').pop() || null;
    if (pageNumber && !Number.isNaN(Number(pageNumber))) {
      return Number(pageNumber);
    }
    return null;
  });

  const { actions, data, loading, page, perPage, count, filters } = useDataList(skillsEndpoint, {
    page: qsPage || 1,
    perPage: IS_SNAP ? SNAP_SHOT_LIMIT : 200, // Increased to show more skills in constellation
    filters: {
      id: urlFilters.id
    },
    sort: {
      occurrence: 'desc'
    }
  });

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleChangeSearch = React.useCallback(
    (e) => {
      actions.onSearchChange(e.target.value);
    },
    [actions]
  );

  const onKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleChangeSearch(e);
      }
    },
    [handleChangeSearch]
  );

  const handleClearSearch = React.useCallback(() => {
    actions.clearData(true);
  }, [actions]);

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);


  // Get skill link URL. getSkillSlug encapsulates the name-lookup chain
  // (name map → name${LANG} sibling → bare Ukrainian `name`) and slugifies.
  const getSkillUrl = React.useCallback(
    (skill) => getLocalizedPath(`/skills/${getSkillSlug(skill)}/${skill._id}`),
    []
  );

  // Get skill name for display
  const getSkillName = React.useCallback(
    (skill) => {
      return skill[`name${lang}`] || skill.name || t('NoName');
    },
    [lang, t]
  );

  // Handle skill click from visualizations
  const handleSkillClick = React.useCallback(
    (skill) => {
      if (skill && skill._id) {
        const url = getSkillUrl(skill);
        navigate(url);
      }
    },
    [navigate, getSkillUrl]
  );

  // Delayed fade-in animation for title/scroll indicator (only for real users, not react-snap)
  React.useEffect(() => {
    if (IS_SNAP) return undefined;
    const titleTimer = setTimeout(() => setTitleVisible(true), 5000);
    return () => clearTimeout(titleTimer);
  }, []);

  React.useEffect(() => {
    if (IS_SNAP) return undefined;
    const scrollIndicatorTimer = setTimeout(() => setScrollIndicatorVisible(true), 10000);
    return () => clearTimeout(scrollIndicatorTimer);
  }, []);

  return (
    <>
      <Helmet page={'skills'} includeHreflang={true} />
      <MainLayout>
        <div style={{ position: 'relative', zIndex: 10 }}>
          <Header transparent={true} />
        </div>
        <Background>
          {/* The animated fixed visualization is expensive and can crash headless Chromium during react-snap. */}
          {!IS_SNAP && data && data.length > 0 ? (
            <VisualizationContainer>
              <VisualizationSection>
                <React.Suspense fallback={<LinearProgress />}>
                  <GalaxyView skills={data} onSkillClick={handleSkillClick} lang={lang} />
                </React.Suspense>
                <ScrollIndicator visible={scrollIndicatorVisible}>
                  <ScrollIndicatorOval>
                    <ScrollDot delay={0} />
                    <ScrollDot delay={0.2} />
                    <ScrollDot delay={0.4} />
                  </ScrollIndicatorOval>
                </ScrollIndicator>
              </VisualizationSection>
            </VisualizationContainer>
          ) : null}

          <OverlayContent>
            <Title variant="h1" visible={titleVisible}>{t('Title')}</Title>
          </OverlayContent>

          <ContentBelow isSnap={IS_SNAP}>
            <ActionsWrapper>
              <TextFieldStyled
                value={filters?.name || ''}
                onChange={handleChangeSearch}
                onKeyDown={onKeyDown}
                placeholder={t('Search')}
                variant="outlined"
                fullWidth
                slotProps={{
                  htmlInput: {
                    'aria-label': t('Search')
                  },
                  input: {
                    endAdornment: (
                      <>
                        {filters?.name ? <ClearIconStyled onClick={handleClearSearch} /> : null}
                        <SearchIcon />
                      </>
                    )
                  }
                }}
              />
            </ActionsWrapper>

            {data && data.length > 0 ? (
              <SkillsList>
                {data.map((skill) => (
                  <SkillItem key={skill._id}>
                    <SkillLink to={getSkillUrl(skill)}>
                      <SkillCategoryIcon skill={skill} size={20} />
                      <SkillName>{getSkillName(skill)}</SkillName>
                    </SkillLink>
                  </SkillItem>
                ))}
              </SkillsList>
            ) : null}

            {data && data.length === 0 && !loading ? <Typography>{t('NoData')}</Typography> : null}

            {!data && loading ? <CardGridSkeleton count={6} /> : null}

            {loading || !data ? <LinearProgress /> : null}

            {limitReached ? null : (
              <>
                <ButtonStyled
                  variant="contained"
                  color="primary"
                  onClick={handleChangePage}
                  aria-label={t('LoadMore')}>
                  {t('LoadMore')}
                </ButtonStyled>
                <Link to={`/skills/page/${page + 1}`} style={{ display: 'none' }} target="_blank" rel="noopener noreferrer">
                  {t('LoadMore')}
                </Link>
              </>
            )}
          </ContentBelow>
        </Background>

        <Footer />
      </MainLayout>
    </>
  );
};

export default Skills;
